// src/context/NotificationQueryContext.js
import React, { createContext, useContext, useState, useCallback } from "react";
import { useQueryClient } from "react-query";

const NotificationQueryContext = createContext();

/**
 * Context provider for notification UI state management using React Query
 * Replaces Redux for UI state while using React Query for server state
 */
export const NotificationQueryProvider = ({ children }) => {
  const queryClient = useQueryClient();

  // UI State (not server state)
  const [filters, setFilters] = useState({
    type: null,
    read: null,
    sort: "createdAt:desc",
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });

  const [selectedNotifications, setSelectedNotifications] = useState([]);

  // Filter management
  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    // Reset to first page when filters change
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      type: null,
      read: null,
      sort: "createdAt:desc",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Pagination management
  const setPage = useCallback((page) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
  }, []);

  // Selection management
  const selectNotification = useCallback((notificationId) => {
    setSelectedNotifications((prev) => {
      const isSelected = prev.includes(notificationId);
      if (isSelected) {
        return prev.filter((id) => id !== notificationId);
      } else {
        return [...prev, notificationId];
      }
    });
  }, []);

  const selectAllNotifications = useCallback(
    (shouldSelectAll, allNotificationIds = []) => {
      if (shouldSelectAll) {
        setSelectedNotifications(allNotificationIds);
      } else {
        setSelectedNotifications([]);
      }
    },
    []
  );

  const clearSelection = useCallback(() => {
    setSelectedNotifications([]);
  }, []);

  // React Query cache management
  const invalidateNotifications = useCallback(() => {
    queryClient.invalidateQueries("notifications");
    queryClient.invalidateQueries("unreadCount");
  }, [queryClient]);

  const refetchNotifications = useCallback(() => {
    return queryClient.refetchQueries(["notifications", filters]);
  }, [queryClient, filters]);

  const refetchUnreadCount = useCallback(() => {
    return queryClient.refetchQueries("unreadCount");
  }, [queryClient]);

  // Combined query parameters for React Query
  const queryParams = {
    ...filters,
    ...pagination,
  };

  const contextValue = {
    // UI State
    filters,
    pagination,
    selectedNotifications,
    queryParams,

    // Filter actions
    updateFilters,
    clearFilters,

    // Pagination actions
    setPage,
    setLimit,

    // Selection actions
    selectNotification,
    selectAllNotifications,
    clearSelection,

    // React Query actions
    invalidateNotifications,
    refetchNotifications,
    refetchUnreadCount,
    queryClient,
  };

  return (
    <NotificationQueryContext.Provider value={contextValue}>
      {children}
    </NotificationQueryContext.Provider>
  );
};

/**
 * Hook to use notification query context
 */
export const useNotificationQueryContext = () => {
  const context = useContext(NotificationQueryContext);
  if (!context) {
    throw new Error(
      "useNotificationQueryContext must be used within a NotificationQueryProvider"
    );
  }
  return context;
};

export default NotificationQueryContext;
